import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Minus, ShoppingBag, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { formatINR } from "../lib/products";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const GROUP_LABEL = {
  lighting: "Lighting",
  build: "Build option",
};

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState({}); // { lighting: 'no_light', build: 'assembled' }
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [selectedColor, setSelectedColor] = useState(0);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        // default to first option in each group
        const defaults = {};
        Object.entries(r.data.options || {}).forEach(([gid, choices]) => {
          if (choices.length > 0) defaults[gid] = choices[0].id;
        });
        setSelected(defaults);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const unitPrice = useMemo(() => {
    if (!product) return 0;
    let p = product.price_inr;
    Object.entries(selected).forEach(([gid, choiceId]) => {
      const choice = (product.options?.[gid] || []).find((c) => c.id === choiceId);
      if (choice) p += choice.price_delta;
    });
    return p;
  }, [product, selected]);

  const originalUnitPrice = useMemo(() => {
    if (!product) return 0;
    let p = product.original_price_inr ?? product.price_inr;
    Object.entries(selected).forEach(([gid, choiceId]) => {
      const choice = (product.options?.[gid] || []).find((c) => c.id === choiceId);
      if (choice) p += choice.original_price_delta ?? choice.price_delta;
    });
    return p;
  }, [product, selected]);

  const handleAdd = () => {
    if (!product) return;
    addItem(product, qty, selected, unitPrice);
    toast.success(`${product.name} added to cart`);
    setOpen(true);
  };

  const gallery =
    product?.images?.length > 1 ? product.images : [product?.image].filter(Boolean);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
          <p className="af-mono text-[10px] uppercase tracking-[0.3em] text-[#FF2800] mb-4">
            // 404
          </p>
          <h1 className="af-display text-4xl font-bold uppercase">
            Frame not found
          </h1>
          <button
            type="button"
            onClick={() => navigate("/shop")}
            className="mt-8 bg-[#FF2800] text-white font-semibold uppercase tracking-wider py-4 px-8"
            data-testid="not-found-shop-button"
          >
            Back to shop
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading || !product) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-32">
          <p className="af-mono text-zinc-500" data-testid="product-loading">
            Loading frame…
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Header />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12">
        <Link
          to="/shop"
          className="af-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 hover:text-white inline-flex items-center gap-2"
          data-testid="back-to-shop-link"
        >
          <ArrowLeft size={14} /> Back to shop
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7 md:sticky md:top-24 self-start">
          <div
            className="af-card relative overflow-hidden aspect-square"
            data-testid="product-image-frame"
          >
            <div
              className="absolute top-0 left-0 right-0 h-[3px] z-10"
              style={{ background: product.team_color }}
            />
            <img
              src={gallery[activeImg]}
              alt={product.name}
              className="w-full h-full object-contain"
            />
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-3 mt-4" data-testid="product-gallery-thumbs">
              {gallery.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 af-card overflow-hidden border transition-colors ${
                    i === activeImg ? "border-[#FF2800]" : "border-white/10 hover:border-white/40"
                  }`}
                  data-testid={`product-gallery-thumb-${i}`}
                >
                  <img src={src} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-5">
          <p
            className="af-mono text-[10px] uppercase tracking-[0.3em]"
            style={{ color: product.team_color }}
            data-testid="product-team-label"
          >
            {product.team_label}
          </p>
          <h1
            className="af-display text-4xl md:text-5xl font-black uppercase tracking-tighter leading-[0.95] mt-4"
            data-testid="product-name"
          >
            {product.name}
          </h1>
          <p className="text-zinc-300 text-lg mt-4">{product.tagline}</p>

          <div className="mt-8 flex items-end gap-4 flex-wrap">
            {product.price_tba ? (
              <>
                <p
                  className="af-display text-4xl font-black text-[#FFD700]"
                  data-testid="product-price-tba"
                >
                  Price TBA
                </p>
                <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 pb-2 w-full">
                  Dropping soon — price to be announced
                </p>
              </>
            ) : (
              <>
                {product.sale_percent > 0 && (
                  <p
                    className="af-display text-2xl font-semibold text-zinc-500 line-through pb-1"
                    data-testid="product-price-original"
                  >
                    {formatINR(originalUnitPrice)}
                  </p>
                )}
                <p
                  className="af-display text-4xl font-black"
                  data-testid="product-price"
                >
                  {formatINR(unitPrice)}
                </p>
                {product.sale_percent > 0 && (
                  <span
                    className="af-mono text-[10px] uppercase tracking-[0.25em] bg-[#FF2800] text-white px-2 py-1 mb-2"
                    data-testid="product-sale-badge"
                  >
                    {Math.round(product.sale_percent)}% OFF
                  </span>
                )}
                <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 pb-2 w-full" data-testid="product-shipping-note">
                  Incl. of all taxes · Shipping not included
                </p>
              </>
            )}
          </div>

          <p className="text-zinc-400 mt-8 leading-relaxed">
            {product.description}
          </p>

          {product.color_options?.length > 0 && (
            <div className="mt-8" data-testid="product-color-options">
              <p className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                // Colourways
              </p>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                {product.color_options.map((c, i) => (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => setSelectedColor(i)}
                    title={c.name}
                    className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${
                      i === selectedColor ? "border-white scale-110" : "border-white/20"
                    }`}
                    style={{ background: c.hex }}
                    data-testid={`color-swatch-${i}`}
                  />
                ))}
              </div>
              <p className="af-mono text-[11px] text-zinc-400 mt-3" data-testid="selected-color-name">
                {product.color_options[selectedColor]?.name}
              </p>
            </div>
          )}

          {product.options?.lighting?.length > 0 && (
            <div
              className="mt-6 inline-flex items-center gap-2 border border-[#FF2800]/40 bg-[#FF2800]/5 px-3 py-2"
              data-testid="model-not-included-banner"
            >
              <span className="af-mono text-[10px] uppercase tracking-[0.25em] text-[#FF2800]">
                Note
              </span>
              <span className="af-mono text-[11px] text-zinc-200">
                Frame only — model not included.
              </span>
            </div>
          )}

          {/* Options */}
          {product.options && (
            <div className="mt-8 space-y-6" data-testid="product-options">
              {Object.entries(product.options).map(([gid, choices]) => (
                <div key={gid}>
                  <p className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
                    // {GROUP_LABEL[gid] || gid}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {choices.map((c) => {
                      const active = selected[gid] === c.id;
                      const delta = c.price_delta;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() =>
                            setSelected((s) => ({ ...s, [gid]: c.id }))
                          }
                          className={`text-left p-3 border transition-colors ${
                            active
                              ? "border-[#FF2800] bg-[#FF2800]/10"
                              : "border-white/15 hover:border-white/40"
                          }`}
                          data-testid={`option-${gid}-${c.id}`}
                          aria-pressed={active}
                        >
                          <p className="af-display text-sm font-semibold">
                            {c.label}
                          </p>
                          <p className="af-mono text-[10px] text-zinc-400 mt-1">
                            {delta === 0
                              ? "Included"
                              : `${delta > 0 ? "+" : "−"} ${formatINR(Math.abs(delta))}`}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 grid grid-cols-2 gap-3">
            {Object.entries(product.specs).map(([k, v]) => (
              <div
                key={k}
                className="border border-white/10 p-4"
                data-testid={`spec-${k.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  {k}
                </p>
                <p className="af-display font-semibold mt-1">{v}</p>
              </div>
            ))}
          </div>

          {product.price_tba ? (
            <div className="mt-10">
              <button
                type="button"
                disabled
                className="w-full inline-flex items-center justify-center gap-2 border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] font-semibold uppercase tracking-wider py-4 px-8 cursor-not-allowed"
                data-testid="dropping-soon-button"
              >
                Dropping soon
              </button>
            </div>
          ) : (
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <div className="inline-flex items-center border border-white/15">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-12 h-14 inline-flex items-center justify-center hover:bg-white/5"
                aria-label="Decrease quantity"
                data-testid="product-qty-decrease"
              >
                <Minus size={14} />
              </button>
              <span
                className="af-mono text-sm w-12 text-center"
                data-testid="product-qty-value"
              >
                {qty}
              </span>
              <button
                type="button"
                onClick={() => setQty((q) => q + 1)}
                className="w-12 h-14 inline-flex items-center justify-center hover:bg-white/5"
                aria-label="Increase quantity"
                data-testid="product-qty-increase"
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              type="button"
              onClick={handleAdd}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF2800] text-white font-semibold uppercase tracking-wider py-4 px-8 hover:bg-[#CC2000] transition-colors"
              data-testid="add-to-cart-button"
            >
              <ShoppingBag size={16} /> Add to cart
            </button>
          </div>
          )}

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              "Pan-India dispatch in 48h",
              product.category === "fortnite"
                ? "3D printed in studio"
                : "Frame only — model not included",
              "Hand-finished in studio",
            ].map((t) => (
              <div
                key={t}
                className="flex items-start gap-2 text-zinc-400 text-xs"
              >
                <Check size={14} className="text-[#00E5FF] mt-0.5" />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
